// Where the probes leaving the solar system are on any date. Their paths are
// JPL Horizons' (public/data/probe-trajectories.json, built by
// scripts/build-probe-trajectories.mjs), read here as cubic Hermite curves
// through position and velocity: heliocentric ecliptic J2000 in AU, JD in
// UT. Before the first moment Horizons covers there is no probe (null).
// After the end of its prediction (2050 or 2100) the path goes on in a
// straight line, which stays within 0.3% of the escape orbit by 2200.
// Pure ESM with no DOM.

// Planet radii in km, for the reach of a flyby.
export const PROBE_PLANET_RADIUS_KM = {
  earth: 6371, jupiter: 71492, saturn: 60268, uranus: 25559, neptune: 24764, pluto: 1188,
};
const AU_KM = 149597870.7;
// A flyby holds the probe to the planet as drawn from FLYBY_NEAR planet
// radii in and lets go by FLYBY_FAR, or by FLYBY_SPREAD times the distance
// it is moved if that is further, so the path bends by a degree at most
// letting go (Pluto as drawn is 550,000 km from Horizons' Pluto, 466 of its
// radii).
export const FLYBY_NEAR = 60;
export const FLYBY_FAR = 600;
export const FLYBY_SPREAD = 90;

function knotIndex(knots, jd) {
  let lo = 0;
  let hi = knots.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (knots[mid][0] <= jd) lo = mid; else hi = mid;
  }
  return lo;
}

// The heliocentric position (AU) at jd into out ([x, y, z]), or null before
// the probe's path begins.
export function probeHelio(track, jd, out) {
  const knots = track.knots;
  if (!knots || !knots.length || !(jd >= knots[0][0])) return null;
  const last = knots[knots.length - 1];
  if (jd >= last[0]) {
    const dt = jd - last[0];
    out[0] = last[1] + last[4] * dt;
    out[1] = last[2] + last[5] * dt;
    out[2] = last[3] + last[6] * dt;
    return out;
  }
  const i = knotIndex(knots, jd);
  const a = knots[i];
  const b = knots[i + 1];
  const h = b[0] - a[0];
  const s = (jd - a[0]) / h;
  const s2 = s * s;
  const s3 = s2 * s;
  const h00 = 2 * s3 - 3 * s2 + 1;
  const h10 = (s3 - 2 * s2 + s) * h;
  const h01 = -2 * s3 + 3 * s2;
  const h11 = (s3 - s2) * h;
  for (let k = 0; k < 3; k++) out[k] = h00 * a[1 + k] + h10 * a[4 + k] + h01 * b[1 + k] + h11 * b[4 + k];
  return out;
}

// Eases from 0 at a to 1 at b with no step in speed or acceleration, so the
// path does not bend sharply where a hold begins or ends.
const smoother = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * t * (t * (6 * t - 15) + 10);
};

// A flyby is in reach for FLYBY_WINDOW days either side of closest approach
// (Voyager 2 took 160 days to leave Saturn's).
export const FLYBY_WINDOW = 400;

// How a flyby passed: the side of the planet at closest approach (unit, AU
// frame) and how far the planet turned the path (radians), from the
// planet-relative motion a day either side of closest approach (whose chord
// a flyby's symmetry keeps parallel to its path there; from launch at the
// Earth, the day after) and 20 days on. Kept per flyby.
const flybyShapes = new WeakMap();
function flybyShape(track, enc, planetAt) {
  const cached = flybyShapes.get(enc);
  if (cached && cached.planetAt === planetAt) return cached;
  const shape = { planetAt, side: [0, 0, 0], bend: 0 };
  const motion = (t0, t1) => {
    const h0 = probeHelio(track, t0, [0, 0, 0]);
    const h1 = probeHelio(track, t1, [0, 0, 0]);
    const p0 = planetAt(enc.planet, t0);
    const p1 = planetAt(enc.planet, t1);
    if (!h0 || !h1 || !p0 || !p1) return null;
    const v = [h1[0] - h0[0] - (p1.x - p0.x), h1[1] - h0[1] - (p1.y - p0.y), h1[2] - h0[2] - (p1.z - p0.z)];
    const n = Math.hypot(v[0], v[1], v[2]);
    return n > 0 ? v.map((x) => x / n) : null;
  };
  const h = probeHelio(track, enc.jd, [0, 0, 0]);
  if (h) {
    const side = [h[0] - enc.at[0], h[1] - enc.at[1], h[2] - enc.at[2]];
    const n = Math.hypot(side[0], side[1], side[2]);
    if (n > 0) shape.side = side.map((x) => x / n);
  }
  const passing = motion(Math.max(enc.jd - 1, track.knots[0][0]), enc.jd + 1);
  const leaving = motion(enc.jd + 19.5, enc.jd + 20.5);
  if (passing && leaving) {
    const cos = passing[0] * leaving[0] + passing[1] * leaving[1] + passing[2] * leaving[2];
    shape.bend = 2 * Math.acos(Math.min(1, Math.max(-1, cos)));
  }
  flybyShapes.set(enc, shape);
  return shape;
}

// The probe at jd with its flybys held to the planets as the viewer draws
// them. The viewer's planets come from JPL's approximate elements, up to a
// million km from Horizons' (Jupiter), more than some flybys' whole
// distance, so near a planet the path is moved by the difference at closest
// approach, in full within FLYBY_NEAR radii of it and fading out as above:
// the probe passes the planet as drawn on the side and at the distance it
// really did. planetAt(name, jd) is the viewer's heliocentric
// position of a planet ({ x, y, z } in AU). Fills and returns out: { x, y,
// z } (AU), helio (Horizons' own position) and, for the flyby in reach (the
// one held most), planet, weight (0 to 1, how far it is held), rel (the
// probe from the planet, AU), at (the planet as drawn, AU), side (the side
// of the planet it passed, unit) and bend (how far the planet turned it,
// radians); null before launch.
export function probePlace(track, jd, planetAt, out) {
  const h = probeHelio(track, jd, out.helio || (out.helio = [0, 0, 0]));
  if (!h) return null;
  out.x = h[0];
  out.y = h[1];
  out.z = h[2];
  out.planet = null;
  out.weight = -1;
  const rel = out.rel || (out.rel = [0, 0, 0]);
  const at = out.at || (out.at = [0, 0, 0]);
  const side = out.side || (out.side = [0, 0, 0]);
  for (const enc of track.encounters || []) {
    const radius = (PROBE_PLANET_RADIUS_KM[enc.planet] || 0) / AU_KM;
    if (!radius || Math.abs(jd - enc.jd) > FLYBY_WINDOW) continue;
    const drawnThen = planetAt(enc.planet, enc.jd);
    const drawnNow = planetAt(enc.planet, jd);
    if (!drawnThen || !drawnNow) continue;
    const ox = drawnThen.x - enc.at[0];
    const oy = drawnThen.y - enc.at[1];
    const oz = drawnThen.z - enc.at[2];
    const rx = h[0] - (drawnNow.x - ox);
    const ry = h[1] - (drawnNow.y - oy);
    const rz = h[2] - (drawnNow.z - oz);
    const far = Math.max(FLYBY_FAR * radius, FLYBY_SPREAD * Math.hypot(ox, oy, oz));
    const w = 1 - smoother(FLYBY_NEAR * radius, far, Math.hypot(rx, ry, rz));
    if (w <= out.weight) continue;
    out.x = h[0] + w * ox;
    out.y = h[1] + w * oy;
    out.z = h[2] + w * oz;
    out.planet = enc.planet;
    out.weight = w;
    rel[0] = rx;
    rel[1] = ry;
    rel[2] = rz;
    at[0] = drawnNow.x;
    at[1] = drawnNow.y;
    at[2] = drawnNow.z;
    const shape = flybyShape(track, enc, planetAt);
    side[0] = shape.side[0];
    side[1] = shape.side[1];
    side[2] = shape.side[2];
    out.bend = shape.bend;
  }
  if (out.weight < 0) out.weight = 0;
  return out;
}
